import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { X, Plus, Search, Microscope, Save, Clock } from 'lucide-react';

/**
 * ManualOrganismForm - Modal form for manually adding organisms to the sample entry
 * 
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback when modal is closed
 * @param {string} manualGroup - Selected group value
 * @param {function} onManualGroupChange - Callback when group changes
 * @param {string} manualSpecies - Selected species value
 * @param {function} onManualSpeciesChange - Callback when species changes
 * @param {string} manualDepth - Depth value
 * @param {function} onManualDepthChange - Callback when depth changes
 * @param {string} groupSearchTerm - Search term for groups
 * @param {function} onGroupSearchChange - Callback when group search changes
 * @param {string} speciesSearchTerm - Search term for species
 * @param {function} onSpeciesSearchChange - Callback when species search changes
 * @param {Array} filteredGroups - Filtered list of groups based on search
 * @param {Array} filteredSpecies - Filtered list of species based on group and search
 * @param {function} onAddClick - Callback when "Legg til" is clicked (receives savePermanently as param)
 * @param {function} onShowAddGroupDialog - Callback to show add group dialog
 * @param {function} onShowAddSpeciesDialog - Callback to show add species dialog
 * @param {string} newGroupName - Name for new group (used in "Legg til" button)
 */
export default function ManualOrganismForm({
  isOpen,
  onClose,
  manualGroup,
  onManualGroupChange,
  manualSpecies,
  onManualSpeciesChange,
  manualDepth,
  onManualDepthChange,
  groupSearchTerm,
  onGroupSearchChange,
  speciesSearchTerm,
  onSpeciesSearchChange,
  filteredGroups,
  filteredSpecies,
  onAddClick,
  onShowAddGroupDialog,
  onShowAddSpeciesDialog,
}) {
  const [savePermanently, setSavePermanently] = useState(false);
  
  if (!isOpen) return null;

  const handleClose = () => {
    setSavePermanently(false);
    onClose();
  };

  const handleAddClick = () => {
    onAddClick(savePermanently);
    setSavePermanently(false);
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" style={{pointerEvents: 'none'}}>
      {/* Backdrop - behind modal */}
      <div 
        className="absolute inset-0 bg-black/50"
        style={{pointerEvents: 'auto'}}
        onClick={handleClose}
      />
      
      {/* Modal content - above backdrop */}
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 relative z-10" style={{pointerEvents: 'auto'}}>
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
        >
          <X className="w-5 h-5" />
        </button>
        
        <h3 className="text-xl font-bold text-slate-900 mb-6">Legg til ny organisme</h3>
        
        <div className="grid grid-cols-1 gap-4">
          {/* Group selector with search and add new */}
          <div>
            <Label className="text-sm font-semibold">Orden/gruppe</Label>
            <div className="flex space-x-2 mt-2">
              <Select 
                value={manualGroup}
                onValueChange={(value) => {
                  onManualGroupChange(value);
                  onManualSpeciesChange('');
                  onSpeciesSearchChange('');
                }}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Velg gruppe..." />
                </SelectTrigger>
                <SelectContent className="z-[200]">
                  {/* Search field */}
                  <div className="p-2 border-b border-slate-200">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={groupSearchTerm}
                        onChange={(e) => onGroupSearchChange(e.target.value)}
                        placeholder="Søk grupper..."
                        className="pl-9 h-9 text-sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  {filteredGroups.map((group) => (
                    <SelectItem key={group} value={group}>
                      <div className="flex items-center space-x-2">
                        <Microscope className="w-4 h-4 text-teal-600" />
                        <span>{group}</span>
                      </div>
                    </SelectItem>
                  ))}
                  {filteredGroups.length === 0 && groupSearchTerm && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onShowAddGroupDialog(groupSearchTerm);
                      }}
                      className="w-full p-3 text-left hover:bg-teal-50 transition-colors border-t"
                    >
                      <div className="flex items-center space-x-2">
                        <Plus className="w-4 h-4 text-teal-600" />
                        <span className="text-sm font-medium">Legg til &quot;{groupSearchTerm}&quot;</span>
                      </div>
                    </button>
                  )}
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                onClick={() => onShowAddGroupDialog('')}
                className="px-3"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Species selector with search and add new */}
          <div>
            <Label className="text-sm font-semibold">Art</Label>
            <div className="flex space-x-2 mt-2">
              <Select 
                value={manualSpecies}
                onValueChange={onManualSpeciesChange}
                disabled={!manualGroup}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder={!manualGroup ? "Velg gruppe først..." : "Velg art..."} />
                </SelectTrigger>
                <SelectContent className="z-[200]">
                  {/* Search field */}
                  <div className="p-2 border-b border-slate-200">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={speciesSearchTerm}
                        onChange={(e) => onSpeciesSearchChange(e.target.value)}
                        placeholder="Søk arter..."
                        className="pl-9 h-9 text-sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  {filteredSpecies.map((species) => (
                    <SelectItem key={species} value={species}>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                        <span>{species}</span>
                      </div>
                    </SelectItem>
                  ))}
                  {filteredSpecies.length === 0 && manualGroup && speciesSearchTerm && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onShowAddSpeciesDialog(speciesSearchTerm);
                      }}
                      className="w-full p-3 text-left hover:bg-teal-50 transition-colors border-t"
                    >
                      <div className="flex items-center space-x-2">
                        <Plus className="w-4 h-4 text-teal-600" />
                        <span className="text-sm font-medium">Legg til &quot;{speciesSearchTerm}&quot;</span>
                      </div>
                    </button>
                  )}
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                onClick={() => onShowAddSpeciesDialog('')}
                disabled={!manualGroup}
                className="px-3"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          <div>
            <Label className="text-sm font-semibold">Djup (meter)</Label>
            <Input 
              type="number"
              placeholder="3"
              value={manualDepth}
              onChange={(e) => onManualDepthChange(e.target.value)}
              className="mt-2"
            />
          </div>
          
          {/* Save permanently toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center space-x-3">
              {savePermanently ? (
                <Save className="w-5 h-5 text-teal-600" />
              ) : (
                <Clock className="w-5 h-5 text-slate-400" />
              )}
              <div>
                <Label className="text-sm font-semibold cursor-pointer" htmlFor="save-permanent">
                  {savePermanently ? 'Lagre permanent i skjema' : 'Kun denne gang'}
                </Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  {savePermanently 
                    ? 'Organismen vil bli lagret i skjemamalen' 
                    : 'Organismen vises kun i denne registreringen'}
                </p>
              </div>
            </div>
            <Switch
              id="save-permanent"
              checked={savePermanently}
              onCheckedChange={setSavePermanently}
            />
          </div>
          
          <div className="flex justify-end space-x-3 mt-4">
            <Button
              onClick={handleClose}
              variant="outline"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleAddClick}
              className={savePermanently ? "bg-purple-600 hover:bg-purple-700" : "bg-teal-600 hover:bg-teal-700"}
              disabled={!manualGroup.trim() || !manualSpecies.trim()}
            >
              {savePermanently ? (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Legg til og lagre
                </>
              ) : (
                'Legg til'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
